import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./sidebar/Sidebar";
import { useAuth } from "@/context/AuthContext";
import ReportDialog from "@/components/widgets/ReportDialog";
import Banner from "./widgets/Banner";
import GenerateDialog from "./widgets/GenerateDialog";
import { PAYMENT_STATUS } from "@/variables";
import { useLandingParams } from "@/hooks/useLandingParams";
import { useReportsStore } from "@/store/store";
import PaidProjectOnboardingDialog from "@/components/widgets/PaidProjectOnboardingDialog.tsx";
import { updateGreeted } from "@/services/userService";
import { SystemBanner } from "@/components/SystemBanner";
import { TrialBanner } from "@/components/TrialBanner";
import { BannerContext } from "@/App";
import {
    consumeStripeReturnPath,
} from "@/lib/stripeNavigation";
import { useReports } from "@/hooks/useReports";
import { useAwaitedReport } from "@/hooks/useAwaitedReport";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useOnboardingGate } from "@/hooks/useOnboardingGate";
import MobileGeneratePreview from "@/components/widgets/MobileGeneratePreview";
import RouteSkeleton from "@/components/skeletons/RouteSkeleton";

export const Layout: React.FC = () => {
    const { user, token, fetchProfile } = useAuth();
    const { companyName, companyUrl } = useLandingParams();
    const navigate = useNavigate();
    const location = useLocation();
    // Subscribe per-field, not to the whole store. Now that the report-progress
    // poll runs in the shell (below), `generatingReports` is replaced every 5s
    // while anything is building — and a bare `useReportsStore()` subscribes to
    // every field, so that tick would re-render Layout and with it the whole
    // routed page. These three change only on user intent.
    const generateDialogRequested = useReportsStore((s) => s.generateDialogRequested);
    const requestedGenerateReportId = useReportsStore((s) => s.requestedGenerateReportId);
    const setGenerateDialogRequest = useReportsStore((s) => s.setGenerateDialogRequest);
    const requestProgressView = useReportsStore((s) => s.requestProgressView);
    const setAwaitedDialogOpen = useReportsStore((s) => s.setAwaitedDialogOpen);
    const [introDialogOpen, setIntroDialogOpen] = useState(false);
    const paidPromptShownRef = useRef(false);
    const bannerData = useContext(BannerContext);
    const mainRef = useRef<HTMLElement>(null);
    const isMobile = useIsMobile();

    // The onboarding VB run/verdict steps render inside this shell (they're
    // route-exempt from the gate below via ?onboarding=1). Anything shell-level
    // that could pull the user elsewhere has to know it's in that flow.
    const inOnboardingFlow =
        location.pathname === "/onboarding" ||
        (location.pathname.includes("/virtual-buyer/") &&
            (location.pathname.includes("/run/") ||
                location.pathname.includes("/verdict/")) &&
            new URLSearchParams(location.search).get("onboarding") === "1");

    // Report-progress polling lives in the shell (AITS-540). It's what fills the
    // store that GenerateDialog and the mobile progress bar read, and both are
    // mounted here — but it used to be mounted only by the Home screen, so
    // "show me this report's progress" had to detour through Home to work at
    // all. Home is gone; the hook self-limits to a 5s poll only while something
    // is actually generating.
    useReports(token);

    // Lands the user on the report they started, whenever it finishes — even if
    // they dismissed the progress dialog, were reading an older month, or were
    // in another window at the time.
    useAwaitedReport();

    // The scroll container is <main>, not the window. React Router doesn't reset
    // its scroll on navigation, so e.g. clicking "Open sources" from the bottom
    // of the report dashboard lands mid-page. Reset to top on pathname change
    // (not search — so the Sources page's ?orbit= filters don't jump to top).
    useEffect(() => {
        mainRef.current?.scrollTo({ top: 0 });
    }, [location.pathname]);

    useEffect(() => {
        if (companyName || companyUrl) {
            setReportDialogOpen(true);
        }
    }, [companyName, companyUrl]);

    useEffect(() => {
        const navState = location.state as {
            openGenerateDialog?: boolean;
            reportId?: number;
            openReportDialog?: boolean;
        } | null;

        let shouldClearState = false;

        if (navState?.openGenerateDialog && navState.reportId) {
            setGenerateDialogRequest(navState.reportId);
            shouldClearState = true;
        }

        if (navState?.openReportDialog) {
            setReportDialogOpen(true);
            shouldClearState = true;
        }

        if (shouldClearState) {
            // Keep the query string. Settings derives its panel purely from
            // `?tab=`, so dropping it here sent "Add a company" from Settings →
            // Companies back to General (AITS-540 made that path reachable by
            // opening the dialog in place instead of routing to Home).
            navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
        }
    }, [location, navigate, setGenerateDialogRequest]);

    useEffect(() => {
        const currentPath = `${location.pathname}${location.search}${location.hash}`;
        const returnPath = consumeStripeReturnPath(currentPath);
        if (returnPath) {
            navigate(returnPath, { replace: true });
        }
    }, [location.hash, location.pathname, location.search, navigate]);

    // Onboarding gate (AITS-590). The status is resolved once per session and
    // the (path-dependent) rule re-evaluated locally on each navigation, so
    // moving between screens no longer costs a round trip — nor a full-screen
    // blocker while it runs. Fetch, invalidation and the fail-closed rule live
    // in the hook.
    const onboardingGate = useOnboardingGate({
        token,
        exempt: inOnboardingFlow,
        pathname: location.pathname,
    });

    const [show, setShow] = React.useState(true);

    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    // Company to seed the report dialog with. The sidebar "+" sets this to the
    // selected company so "run a new report" defaults to it; every other opener
    // leaves it null for a blank form. Cleared on close so a later blank open
    // (or the landing-param flow) can't inherit a stale company.
    const [reportPrefill, setReportPrefill] = useState<{ name?: string; url?: string } | null>(null);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
    // Putting the progress dialog away is remembered for the tab, so a refresh
    // doesn't push it back in the user's face — while still leaving the report
    // awaited, so they're taken to it when it lands.
    //
    // Stable identity matters: this is GenerateDialog's `setOpen`, which sits in
    // the deps of its completion effect. Layout re-renders on every 5s progress
    // tick, and an inline handler would re-run that effect each tick — clearing
    // and rescheduling the dialog's own close timer.
    const handleGenerateDialogOpenChange = useCallback((open: boolean) => {
        setGenerateDialogOpen(open);
        setAwaitedDialogOpen(open);
    }, [setAwaitedDialogOpen]);
    const [currentReportId, setCurrentReportId] = useState<number | null>(null);

    const openReportDialog = (prefill?: { name?: string; url?: string }) => {
        setReportPrefill(prefill ?? null);
        setReportDialogOpen(true);
    };

    const handleReportDialogOpenChange = (open: boolean) => {
        setReportDialogOpen(open);
        if (!open) setReportPrefill(null);
    };

    const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
        if (typeof window === "undefined") return false;
        return localStorage.getItem("sidebarCollapsed") === "1";
    });

    useEffect(() => {
        localStorage.setItem("sidebarCollapsed", sidebarCollapsed ? "1" : "0");
    }, [sidebarCollapsed]);

    useEffect(() => {
        if (user?.should_be_greeted) {
            setIntroDialogOpen(true);
            paidPromptShownRef.current = true;
        }
    }, [user?.should_be_greeted]);

    // When modal opens due to greet flag, notify backend and refresh user; do not close modal programmatically
    useEffect(() => {
        if (!introDialogOpen || !user?.should_be_greeted) return;
        let cancelled = false;
        ;(async () => {
            try {
                await updateGreeted();
            } catch (e) {
                console.error("Failed to update greeted flag", e);
            } finally {
                if (!cancelled) {
                    try {
                        await fetchProfile();
                    } catch (e) {
                        console.error("Failed to refetch user profile", e);
                    }
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [introDialogOpen, user?.should_be_greeted, fetchProfile]);

    useEffect(() => {
        if (generateDialogRequested && requestedGenerateReportId && !generateDialogOpen) {
            setCurrentReportId(requestedGenerateReportId);
            setGenerateDialogOpen(true);
        }
    }, [generateDialogRequested, requestedGenerateReportId, generateDialogOpen]);

    const PaymentStatus = user?.payment_status ? PAYMENT_STATUS[user.payment_status] : null;

    // Redirect from render rather than an effect: the old code cleared the
    // blocker first and then navigated, which rendered one frame of the page it
    // was about to leave.
    if (onboardingGate === "redirect") {
        return <Navigate to="/onboarding" replace />;
    }

    return (
        <div className="h-screen w-screen flex flex-col overflow-hidden">
            {bannerData && bannerData.is_active && (
                <div className="w-full shrink-0">
                    <SystemBanner message={bannerData.message} level={bannerData.level} />
                </div>
            )}
           
            {PaymentStatus && (
                <div className="w-full shrink-0 relative z-20">
                    <Banner
                        open={show}
                        onClose={() => setShow(false)}
                        variant="warning"
                        sticky={false}
                        isFixed={false}
                        className="w-full"
                    >
                        {PaymentStatus}
                    </Banner>
                </div>
            )}
            <div 
                className="flex flex-col sm:flex-row flex-1 w-full overflow-hidden relative" 
            >
                <PaidProjectOnboardingDialog open={introDialogOpen} onOpenChange={setIntroDialogOpen} />
            <ReportDialog
                open={reportDialogOpen}
                setOpen={handleReportDialogOpenChange}
                onClick={(open, reportId) => {
                    // Through the store, not just local state: this is what
                    // claims the report as "awaited", so the shell takes the
                    // user to it on completion whether or not this dialog is
                    // still open by then.
                    if (reportId) setGenerateDialogRequest(reportId);
                    setCurrentReportId(reportId);
                    setGenerateDialogOpen(open);
                }}
                initialCompanyName={reportPrefill?.name ?? companyName}
                initialCompanyUrl={reportPrefill?.url ?? companyUrl}
            />
            <GenerateDialog
                open={generateDialogOpen}
                setOpen={handleGenerateDialogOpenChange}
                reportId={currentReportId}
            />
            {/* Mobile counterpart of GenerateDialog — a pinned bar for reports
                still building. Renders nothing when none are, so it's safe
                app-wide now that it no longer hangs off the Home screen.
                Suppressed inside onboarding: the VB run/verdict steps render in
                this shell, and the bar would sit over them advertising the AI
                Trust Signals report building in the background — tapping it opens the
                progress dialog, which navigates to that report on completion and
                walks the user straight out of setup. */}
            {isMobile && !inOnboardingFlow && (
                <MobileGeneratePreview
                    onReportClick={(report) => requestProgressView(report.id)}
                />
            )}

            <Sidebar
                onOpenReportDialog={openReportDialog}
                collapsed={sidebarCollapsed}
                onCollapsedChange={setSidebarCollapsed}
            />

            <main
                ref={mainRef}
                className="w-full flex-1 overflow-y-auto overflow-x-hidden"
            >
                {/* App-shell frame: cap width + center. Each page owns its own
                    gutter via the .vb-page / .vb-page--narrow body wrappers
                    (AITS-489); the capability screens keep their own. */}
                <TrialBanner />
                <div className="w-full max-w-[1540px] mx-auto">
                    {/* Cold pass only — the shell keeps its chrome and the routed
                        page is stood in for by a skeleton of itself, instead of
                        the whole app blanking to "Checking access…". */}
                    {onboardingGate === "resolving" ? (
                        <RouteSkeleton pathname={location.pathname} />
                    ) : (
                        <Outlet />
                    )}
                </div>
            </main>
        </div>
        </div>
    );
};

export default Layout;
